import { heroStats } from '@/mocks/site';

type HeroProps = {
  query: string;
  onQueryChange: (value: string) => void;
  onSearch: () => void;
};

const quickChips = ['2025新片', '4K蓝光原盘', '迅雷下载', '磁力链接', '高分悬疑', '合家欢动画'];

export default function Hero({ query, onQueryChange, onSearch }: HeroProps) {
  return (
    <section id="home" className="scroll-mt-20 bg-background-100">
      <div className="w-full px-4 md:px-6 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200 text-primary-700 text-xs md:text-sm">
              <i className="ri-fire-line"></i>
              2025 新片 · 蓝光原盘 · 迅雷磁力
            </span>

            <h1 className="mt-5 font-heading text-3xl md:text-5xl font-bold leading-tight text-foreground-950">
              电影天堂
              <span className="block mt-1 text-primary-600 text-2xl md:text-3xl">
                最新电影高清下载，一站找齐
              </span>
            </h1>

            <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700 max-w-xl">
              这里是影迷的高清资源聚合站。首页集中展示<strong className="text-foreground-900 font-semibold">最新上线影片、蓝光原盘专区与热门排行榜</strong>，
              收录 2025 年新片、经典修复版与高分剧集。通过题材标签与关键词即可快速检索心仪作品，
              并对照分辨率、码率与体积挑选合适的版本，享受流畅的下载与观影体验。
            </p>

            <div className="mt-6 max-w-xl">
              <div className="flex flex-col sm:flex-row gap-2 rounded-lg border border-background-300 bg-background-50 p-2">
                <div className="flex items-center gap-2 flex-1 px-2">
                  <i className="ri-search-line text-foreground-400 text-lg"></i>
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onSearch();
                    }}
                    placeholder="输入片名、导演或题材，如「星海迷航」"
                    aria-label="搜索影片"
                    className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 py-2 focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={onSearch}
                  className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  搜索影片
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">热门：</span>
                {quickChips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => onQueryChange(chip)}
                    className="px-2.5 py-1 text-xs rounded-full bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>

            <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
              {heroStats.map((stat) => (
                <div key={stat.label} className="border-l-2 border-accent-400 pl-3">
                  <dt className="sr-only">{stat.label}</dt>
                  <dd>
                    <span className="block font-heading text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</span>
                    <span className="block mt-1 text-xs text-foreground-500">{stat.label}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative animate-fade-up">
            <div className="relative w-full h-[320px] md:h-[460px] rounded-lg overflow-hidden border border-background-300">
              <img
                src="https://readdy.ai/api/search-image?query=stylized%20cinematic%20illustration%20of%20a%20cozy%20modern%20home%20theater%20with%20a%20large%20glowing%20screen%2C%20warm%20amber%20ambient%20light%20and%20deep%20crimson%20accents%2C%20film%20reels%20and%20projector%20glow%2C%20dark%20rich%20background%2C%20film%20grain%20texture%2C%20minimal%20elegant%20composition%2C%20high%20detail&width=1000&height=820&seq=hero-cinema-room&orientation=landscape"
                alt="家庭影院场景示意图，暖色灯光下的观影空间"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
                <div className="text-background-50">
                  <p className="text-xs opacity-80">正在热映 · 4K 蓝光</p>
                  <p className="font-heading text-lg font-semibold">星海迷航</p>
                </div>
                <span className="px-3 py-1.5 rounded-md bg-accent-500 text-foreground-950 text-xs font-medium whitespace-nowrap">
                  8.7 分
                </span>
              </div>
            </div>

            <div className="absolute -bottom-6 left-4 md:left-8 bg-background-50 border border-background-200 rounded-lg px-4 py-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-secondary-50 text-secondary-600 flex items-center justify-center">
                <i className="ri-download-cloud-2-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">今日新增 12 部</p>
                <p className="text-xs text-foreground-500">蓝光原盘 · 迅雷磁力</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}